import { useParams } from 'react-router-dom';
import { getMovieById, getRelatedMovies, categories } from '@/mocks/movies';
import MovieCard from '@/components/feature/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movieId = parseInt(id || '0', 10);
  const movie = getMovieById(movieId);

  if (!movie) {
    return (
      <main className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
            <i className="ri-error-warning-line text-foreground-400 text-3xl"></i>
          </div>
          <h2 className="text-xl font-heading text-foreground-900 mb-2">内容未找到</h2>
          <p className="text-sm text-foreground-500 mb-4">抱歉，您要查看的影视内容不存在或已下架</p>
          <Link
            to="/"
            className="inline-flex items-center gap-1 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const relatedMovies = getRelatedMovies(movie);
  const catInfo = categories.find((c) => c.slug === movie.category);

  const schemaArticle = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    alternateName: movie.titleEn,
    description: movie.description,
    image: movie.poster,
    dateCreated: movie.releaseDate,
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.actors.map((actor) => ({
      '@type': 'Person',
      name: actor,
    })),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: movie.rating,
      bestRating: '10',
    },
    countryOfOrigin: {
      '@type': 'Country',
      name: movie.region,
    },
    url: `https://www.fanqieyingshi.com/article/${movie.id}`,
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(schemaArticle)}
      </script>

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: movie.categoryLabel, href: `/category/${movie.category}` },
            { label: movie.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-6">
          <div className="max-w-[1280px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0 w-full lg:w-[320px]">
                <div className="rounded-lg overflow-hidden bg-background-100 aspect-[2/3] lg:sticky lg:top-24">
                  <img
                    src={movie.poster}
                    alt={`${movie.title} - ${movie.titleEn}`}
                    title={`${movie.title} 高清免费在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold whitespace-nowrap">
                    {movie.categoryLabel}
                  </span>
                  {movie.isHot && (
                    <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold whitespace-nowrap">
                      热播
                    </span>
                  )}
                  {movie.isNew && (
                    <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold whitespace-nowrap">
                      新上架
                    </span>
                  )}
                  <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold whitespace-nowrap">
                    {movie.status}
                  </span>
                </div>

                <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-1">
                  {movie.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-4">{movie.titleEn}</p>

                <div className="flex items-center gap-4 mb-6">
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500"></i>
                    <span className="text-lg font-bold text-accent-600">{movie.rating}</span>
                    <span className="text-xs text-foreground-400">/ 10</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                  <div className="p-3 rounded-lg bg-background-100">
                    <p className="text-xs text-foreground-500 mb-0.5">导演</p>
                    <p className="text-sm font-medium text-foreground-900">{movie.director}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-background-100">
                    <p className="text-xs text-foreground-500 mb-0.5">主演</p>
                    <p className="text-sm font-medium text-foreground-900 line-clamp-1">
                      {movie.actors.join('、')}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-background-100">
                    <p className="text-xs text-foreground-500 mb-0.5">地区</p>
                    <p className="text-sm font-medium text-foreground-900">{movie.region}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-background-100">
                    <p className="text-xs text-foreground-500 mb-0.5">
                      {movie.category === 'tv' || movie.category === 'variety' || movie.category === 'anime' ? '集数' : '时长'}
                    </p>
                    <p className="text-sm font-medium text-foreground-900">{movie.duration}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-background-100">
                    <p className="text-xs text-foreground-500 mb-0.5">年份</p>
                    <p className="text-sm font-medium text-foreground-900">{movie.year}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-background-100">
                    <p className="text-xs text-foreground-500 mb-0.5">状态</p>
                    <p className="text-sm font-medium text-foreground-900">{movie.status}</p>
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">标签</h3>
                  <div className="flex flex-wrap gap-2">
                    {movie.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">剧情简介</h3>
                  <div
                    className="text-sm text-foreground-700 leading-relaxed space-y-2"
                    dangerouslySetInnerHTML={{ __html: movie.content }}
                  />
                </div>

                <div className="flex items-center gap-3">
                  <Link
                    to={`/category/${movie.category}`}
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-play-circle-line"></i>
                    立即观看
                  </Link>
                  <Link
                    to={`/category/${movie.category}`}
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-background-100 text-foreground-800 text-sm font-medium border border-background-200 hover:border-primary-300 hover:text-primary-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    返回{movie.categoryLabel}列表
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </div>

            {relatedMovies.length > 0 && (
              <section className="mt-12 pt-8 border-t border-background-200">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-lg md:text-xl font-heading text-foreground-950">
                      <i className="ri-thumb-up-line text-primary-500 mr-2"></i>
                      相关推荐
                    </h2>
                    <p className="text-sm text-foreground-500 mt-1">根据你的喜好，为你推荐更多精彩内容</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
    </>
  );
}